import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, maxLength, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { combineLatest, firstValueFrom, from, map, Observable, startWith, Subject, takeUntil } from 'rxjs'

import { PushIlmoitusManuaalinenLahetysPyynto, PushIlmoitusManuaalinenLahetysVastaus } from '@lemon/core-firebase/model/push-ilmoitus'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { ListausAsiakas } from 'app/_jaettu-lemonator/model/asiakas'

interface PushIlmoitusLomake {
  otsikko: string
  teksti: string
}

const TYHJA_LOMAKE: PushIlmoitusLomake = { otsikko: '', teksti: '' }

@Component({
  templateUrl: './push-ilmoitus-tyokalu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PushIlmoitusTyokaluComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _lahetysInflight = false
  private readonly _injector = inject(Injector)

  asiakasHaku = form(signal<ListausAsiakas | string>(''))
  suodatetutAsiakkaatObservable: Observable<ListausAsiakas[]>
  readonly valittuAsiakas = signal<ListausAsiakas | null>(null)

  lomake = form(signal<PushIlmoitusLomake>(TYHJA_LOMAKE), polku => {
    required(polku.otsikko)
    maxLength(polku.otsikko, 100)
    required(polku.teksti)
    maxLength(polku.teksti, 500)
  })

  readonly commonError = signal<string | null>(null)

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _dialog: MatDialog,
  ) {}

  ngOnInit() {
    const kaikkiAsiakkaatPromise = this._firebaseLemonator.firestoreCollectionGroup<ListausAsiakas>('asiakaslistaus').get()
    this.suodatetutAsiakkaatObservable = combineLatest([from(kaikkiAsiakkaatPromise), arvonMuutokset(this.asiakasHaku, { injector: this._injector }).pipe(startWith(''))]).pipe(
      map(([asiakkaat, haku]) => {
        if (typeof haku !== 'string') {
          return []
        }
        const teksti = haku.trim().toLowerCase()
        if (teksti.length < 2) {
          return []
        }
        return asiakkaat
          .filter(asiakas => (asiakas.n || '').toLowerCase().includes(teksti) || (asiakas.y || '').toLowerCase().includes(teksti) || String(asiakas.i ?? '').includes(teksti))
          .slice(0, 25)
      }),
      takeUntil(this._ngUnsubscribe),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  asiakasDisplay = (asiakas: ListausAsiakas | string): string => {
    if (typeof asiakas === 'string') {
      return asiakas
    }
    return asiakas ? `${asiakas.n} (${asiakas.y})` : ''
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  valitseAsiakas(asiakas: ListausAsiakas) {
    this.valittuAsiakas.set(asiakas)
  }

  tyhjennaAsiakas() {
    this.valittuAsiakas.set(null)
    asetaArvo(this.asiakasHaku, '')
  }

  async laheta() {
    this.lomake().markAsTouched()
    if (this.lomake().invalid() || !this.valittuAsiakas()) {
      return
    }
    if (this._lahetysInflight) {
      return
    }
    this._lahetysInflight = true

    try {
      const areYouSureData: AreYouSureDialogData = {
        header: 'Lähetä push-ilmoitus asiakkaalle',
        text: `Push-ilmoitus lähetetään asiakkaan "${this.valittuAsiakas().n}" kaikkien aktiivisten käyttäjien mobiililaitteisiin. Oletko varma?`,
        rightAction: 'Lähetä',
      }
      const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: areYouSureData }).afterClosed())
      if (!sure) {
        return
      }

      this._ladataanService.aloitaLataaminen()
      this.commonError.set(null)
      try {
        const pyynto: PushIlmoitusManuaalinenLahetysPyynto = {
          otsikko: this.lomake.otsikko().value().trim(),
          teksti: this.lomake.teksti().value().trim(),
          tyyppi: 'yleinen-viesti',
          asiakasAvain: this.valittuAsiakas().k,
        }
        const vastaus = await this._firebaseLemonator.functionsCall<PushIlmoitusManuaalinenLahetysPyynto, PushIlmoitusManuaalinenLahetysVastaus>('pushIlmoitusManuaalinenLahetys', pyynto)
        if (vastaus.e) {
          this.commonError.set('Push-ilmoituksen lähettämisessä tapahtui virhe. Ole hyvä ja yritä uudelleen.')
          return
        }
        this._snackbar.open(`Push-ilmoitus lähetetty asiakkaan "${this.valittuAsiakas().n}" aktiivisille käyttäjille.`, 'OK', { duration: 5000 })
        nollaa(this.lomake, TYHJA_LOMAKE)
        this.tyhjennaAsiakas()
      } catch (err) {
        this.commonError.set('Push-ilmoituksen lähettämisessä tapahtui virhe. Ole hyvä ja yritä uudelleen.')
        this._errorHandler.handleError(err)
      } finally {
        this._ladataanService.lopetaLataaminen()
      }
    } finally {
      this._lahetysInflight = false
    }
  }
}
